/**
 * Polygon offset using Clipper library — produces clean results without self-intersections.
 * Used for fringe around greens. Falls back to no-op until Clipper is loaded.
 */

import {
  loadNativeClipperLibInstanceAsync,
  EndType,
  JoinType,
  NativeClipperLibRequestedFormat,
  ClipType,
  PolyFillType,
  type Path,
} from 'js-angusj-clipper';
import type { Point } from './types';

const SCALE = 100; // mm → integer (preserve 2 decimal places)

let clipperLib: Awaited<ReturnType<typeof loadNativeClipperLibInstanceAsync>> | null = null;

/** Initialize Clipper (call at app startup). */
export async function initClipper(): Promise<void> {
  if (clipperLib) return;
  clipperLib = await loadNativeClipperLibInstanceAsync(
    NativeClipperLibRequestedFormat.WasmWithAsmJsFallback
  );
}

function toClipperPath(points: Point[]): Path {
  return points.map(([x, y]) => ({
    x: Math.round(x * SCALE),
    y: Math.round(y * SCALE),
  }));
}

function fromClipperPath(path: Path): Point[] {
  return path.map((p) => [p.x / SCALE, p.y / SCALE] as Point);
}

/** Signed area (doubled). Positive = CCW, negative = CW (y-up). */
function signedArea2(points: Point[]): number {
  let a = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p = points[i];
    const prev = points[(i - 1 + n) % n];
    const next = points[(i + 1) % n];
    if (!p || !prev || !next) continue;
    a += p[0] * (next[1] - prev[1]);
  }
  return a;
}

/**
 * Offset a closed polygon outward by offsetMm. Uses Clipper for clean results.
 * Returns the first result polygon, or [] if Clipper not loaded or offset fails.
 */
export function polygonOffsetOutward(points: Point[], offsetMm: number): Point[] {
  if (points.length < 3 || !clipperLib) return [];

  const path = toClipperPath(points);
  // CCW: positive delta expands outward. CW: negative delta expands outward.
  const sign = signedArea2(points) >= 0 ? 1 : -1;
  const delta = Math.round(offsetMm * SCALE * sign);
  if (delta === 0) return [];

  const result = clipperLib.offsetToPaths({
    delta,
    offsetInputs: [
      {
        data: path,
        joinType: JoinType.Round,
        endType: EndType.ClosedPolygon,
      },
    ],
  });

  if (!result || result.length === 0) return [];
  return fromClipperPath(result[0]!);
}

/**
 * Subtract clip polygon from subject. Returns polygons that are in subject but not in clip.
 * Uses Clipper Difference. Returns [] if Clipper not loaded or result is empty.
 */
export function polygonDifference(subject: Point[], clip: Point[]): Point[][] {
  if (subject.length < 3 || clip.length < 3 || !clipperLib) return [];

  const subjectPath = toClipperPath(subject);
  const clipPath = toClipperPath(clip);

  const result = clipperLib.clipToPaths({
    clipType: ClipType.Difference,
    subjectFillType: PolyFillType.EvenOdd,
    subjectInputs: [{ data: subjectPath, closed: true }],
    clipInputs: [{ data: clipPath }],
  });

  if (!result || result.length === 0) return [];
  return result.filter((path) => path.length >= 3).map((path) => fromClipperPath(path));
}
